import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { TYPE_CONTACT_OPTIONS, optionRole } from '../constants/contactEnums';
import { syncContactTagsByType } from '../utils/contactProfile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Les quatre rôles courants de la saisie rapide. Le code est ce qui est enregistré.
const ROLES_RAPIDES = ['acquereur', 'vendeur', 'bailleur'];

const CHAMPS = [
  { cle: 'prenom', label: 'Prénom', type: 'text', mode: 'given-name' },
  { cle: 'nom', label: 'Nom', type: 'text', mode: 'family-name', requis: true },
  { cle: 'telephone', label: 'Téléphone', type: 'tel', mode: 'tel' },
  { cle: 'email', label: 'E-mail', type: 'email', mode: 'email' },
  { cle: 'nom_societe', label: 'Société', type: 'text', mode: 'organization' },
];

export default function ContactExpressPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const retour = params.get('retour');
  const depuisVisite = Boolean(retour);

  const [role, setRole] = useState(
    depuisVisite ? 'acquereur' : (optionRole(params.get('type'))?.value || 'acquereur'),
  );
  const [titre, setTitre] = useState('');
  const [valeurs, setValeurs] = useState(() => ({
    prenom: '', nom: params.get('nom') || '', telephone: '', email: '',
    nom_societe: '', note_interne: '',
  }));
  const [enCours, setEnCours] = useState(false);
  const [doublon, setDoublon] = useState(null);

  const majValeur = (cle, v) => setValeurs((prev) => ({ ...prev, [cle]: v }));
  const valide = valeurs.nom.trim() && (valeurs.telephone.trim() || valeurs.email.trim());

  const charge = () => syncContactTagsByType({
    type_contact: role,
    titre,
    prenom: valeurs.prenom.trim(),
    nom: valeurs.nom.trim(),
    nom_societe: valeurs.nom_societe.trim(),
    note_interne: valeurs.note_interne.trim(),
    telephones: valeurs.telephone.trim()
      ? [{ numero: valeurs.telephone.trim(), type: 'mobile', principal: true }] : [],
    emails: valeurs.email.trim()
      ? [{ email: valeurs.email.trim(), type: 'personnel', principal: true }] : [],
    groupes: [],
  });

  const apresCreation = (contactId) => {
    if (depuisVisite) {
      const separateur = retour.includes('?') ? '&' : '?';
      navigate(`${retour}${separateur}contact=${contactId}`);
      return;
    }
    navigate(`/contacts/${contactId}`);
  };

  const creer = async () => {
    setEnCours(true);
    try {
      const { data } = await axios.post(`${API}/contacts`, charge());
      toast.success('Contact créé');
      apresCreation(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setEnCours(false);
    }
  };

  const enregistrer = async () => {
    if (!valide) {
      toast.error('Nom et au moins un téléphone ou un e-mail');
      return;
    }
    setEnCours(true);
    try {
      const { data } = await axios.post(`${API}/contacts/verifier-doublon`, {
        type: 'personne_physique',
        email: valeurs.email.trim(),
        telephone: valeurs.telephone.trim(),
      });
      if (data?.doublon) {
        setDoublon(data.doublon);
        setEnCours(false);
        return;
      }
    } catch {
      // La vérification n'est pas bloquante : on continue vers la création.
    }
    setEnCours(false);
    await creer();
  };

  const ficheComplete = () => {
    const q = new URLSearchParams({ nom: valeurs.nom.trim() });
    if (role) q.set('type', role);
    if (retour) q.set('retour', retour);
    navigate(`/contacts/new?${q.toString()}`);
  };

  const champ = (c) => (
    <div key={c.cle}>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor={`express-${c.cle}`}>
        {c.label}{c.requis ? ' *' : ''}
      </label>
      <input
        id={`express-${c.cle}`}
        type={c.type}
        autoComplete={c.mode}
        value={valeurs[c.cle]}
        onChange={(e) => majValeur(c.cle, e.target.value)}
        data-testid={`express-${c.cle}`}
        className="w-full h-11 px-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
      />
    </div>
  );

  return (
    <div className="min-h-full bg-slate-50" data-testid="contact-express-page">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="express-back-btn"
          className="w-11 h-11 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center"
        >
          <i className="bi bi-arrow-left text-lg" />
        </button>
        <h1 className="text-base md:text-lg font-bold text-slate-900">
          {depuisVisite ? 'Nouvel acquéreur' : 'Contact express'}
        </h1>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {depuisVisite && (
          <p className="bg-blue-50 text-blue-800 text-sm rounded-xl px-4 py-3" data-testid="express-rappel-visite">
            Ce contact sera l'acquéreur de la visite en cours de saisie.
          </p>
        )}

        <section className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
          {!depuisVisite && (
            <div>
              <span className="block text-sm font-semibold text-slate-700 mb-1.5">Rôle</span>
              <div className="grid grid-cols-2 gap-2">
                {ROLES_RAPIDES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setRole(code)}
                    data-testid={`express-role-${code}`}
                    className={`h-11 rounded-lg text-sm font-semibold border transition ${
                      role === code
                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                        : 'border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    {optionRole(code)?.label || code}
                  </button>
                ))}
                {/* « Autre… » : liste complète en <select> natif, présenté comme un bouton. */}
                <div className="relative">
                  <select
                    value={ROLES_RAPIDES.includes(role) ? '' : role}
                    onChange={(e) => setRole(e.target.value)}
                    data-testid="express-role-autre"
                    className={`w-full h-11 rounded-lg text-sm font-semibold border text-center appearance-none px-2 ${
                      ROLES_RAPIDES.includes(role)
                        ? 'border-slate-200 text-slate-700'
                        : 'border-blue-500 bg-blue-50 text-blue-800'
                    }`}
                  >
                    <option value="">Autre…</option>
                    {TYPE_CONTACT_OPTIONS.filter((o) => !ROLES_RAPIDES.includes(o.value)).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div>
            <span className="block text-sm font-semibold text-slate-700 mb-1.5">Civilité</span>
            <div className="grid grid-cols-2 gap-2">
              {['M.', 'Mme'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTitre(titre === t ? '' : t)}
                  data-testid={`express-titre-${t === 'M.' ? 'm' : 'mme'}`}
                  className={`h-11 rounded-lg text-sm font-semibold border transition ${
                    titre === t
                      ? 'border-blue-500 bg-blue-50 text-blue-800'
                      : 'border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {CHAMPS.map(champ)}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="express-note">
              Note
            </label>
            <textarea
              id="express-note"
              rows={3}
              value={valeurs.note_interne}
              onChange={(e) => majValeur('note_interne', e.target.value)}
              data-testid="express-note"
              className="w-full px-3 py-2 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </section>

        {/* Téléphone : espace au-dessus de la barre « Enregistrer » et du menu du bas. */}
        <div
          className="md:hidden"
          style={{ height: 'calc(138px + env(safe-area-inset-bottom, 0px))' }}
          aria-hidden="true"
        />
      </main>

      <footer
        className="fixed md:static inset-x-0 bg-white border-t border-slate-200 md:border-0 md:bg-transparent px-4 py-3 md:px-0 md:pt-0 z-30"
        style={{ bottom: 'calc(58px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-2xl mx-auto md:px-4">
          <button
            type="button"
            onClick={enregistrer}
            disabled={enCours || !valide}
            data-testid="express-submit-btn"
            className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold"
          >
            {enCours ? 'Enregistrement…'
              : depuisVisite ? 'Enregistrer et revenir à la visite' : 'Enregistrer'}
          </button>
          <button
            type="button"
            onClick={ficheComplete}
            data-testid="express-fiche-complete-btn"
            className="w-full h-11 mt-1 text-sm font-semibold text-slate-600 underline"
          >
            Compléter la fiche complète
          </button>
        </div>
      </footer>

      {doublon && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" data-testid="express-doublon-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4">
            <h2 className="text-base font-bold text-slate-900">Ce contact existe peut-être déjà</h2>
            <div className="mt-3 bg-slate-50 rounded-xl px-3 py-3">
              <p className="font-semibold text-slate-900" data-testid="express-doublon-nom">
                {doublon.nom_affichage}
              </p>
              <p className="text-sm text-slate-500">
                {[doublon.role, doublon.telephone, doublon.email].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={() => apresCreation(doublon.id)}
                data-testid="express-doublon-utiliser-btn"
                className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold"
              >
                Utiliser ce contact
              </button>
              <button
                type="button"
                onClick={() => { setDoublon(null); creer(); }}
                data-testid="express-doublon-creer-btn"
                className="w-full h-12 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold"
              >
                Non, créer un nouveau contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
